import React, { useMemo, useEffect } from "react";
import {
  MenuItem,
  Select,
  SelectChangeEvent,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  useReportedPeriodsByUser,
  usePreAdviceReportedPeriodsByUser,
  useReportedPeriodsByMultipleContacts,
  usePreAdviceReportedPeriodsByMultipleContacts,
} from "../../../hooks/useReportedPeriods";
import { formatIsoDate } from "../../../utils/namesFormater";
import { ReportedPeriod } from "../../../interfaces/DTO/agent/mimshakGroupBy";

export interface PeriodOption {
  reported_period: string;
  policy_count: number;
  type: "REGULAR" | "PRE_ADVICE";
}

interface PeriodDropdownSelectorProps {
  userId?: number;
  contactIds?: number[]; // ✅ תמיכה במספר contacts
  value?: string;
  type?: "REGULAR" | "PRE_ADVICE";
  onValueChange?: (period: string, type: "REGULAR" | "PRE_ADVICE") => void;
  loadPreAdvice?: boolean;
  showPolicyCount?: boolean;
  onPeriodsLoaded?: (periods: ReportedPeriod[]) => void;
}

export const PeriodDropdownSelector: React.FC<PeriodDropdownSelectorProps> = ({
  userId,
  contactIds,
  value,
  type,
  onValueChange,
  loadPreAdvice = true,
  showPolicyCount = true,
  onPeriodsLoaded,
}) => {
  // ✅ אם יש contactIds - השתמש ב-batch hooks, אחרת ב-single user hooks
  const useBatchMode = contactIds && contactIds.length > 0;

  const { data: singleReportedPeriods = [], isLoading: loadingSingleRegular } = useReportedPeriodsByUser(userId!);
  const { data: singlePreAdvicePeriods = [], isLoading: loadingSinglePreAdvice } = usePreAdviceReportedPeriodsByUser(userId!);

  const { data: batchReportedPeriods = [], isLoading: loadingBatchRegular } = useReportedPeriodsByMultipleContacts(contactIds || []);
  const { data: batchPreAdvicePeriods = [], isLoading: loadingBatchPreAdvice } = usePreAdviceReportedPeriodsByMultipleContacts(contactIds || []);

  const reportedPeriods = useBatchMode ? batchReportedPeriods : singleReportedPeriods;
  const preAdvicePeriods = useBatchMode ? batchPreAdvicePeriods : singlePreAdvicePeriods;
  const loadingRegular = useBatchMode ? loadingBatchRegular : loadingSingleRegular;
  const loadingPreAdvice = useBatchMode ? loadingBatchPreAdvice : loadingSinglePreAdvice;

  const combinedPeriods = useMemo<PeriodOption[]>(() => {
    const regular = reportedPeriods.map(p => ({ ...p, type: "REGULAR" as const }));
    const preAdvice = loadPreAdvice
      ? preAdvicePeriods.map(p => ({ ...p, type: "PRE_ADVICE" as const }))
      : [];
    return [...regular, ...preAdvice].sort((a, b) =>
      b.reported_period.localeCompare(a.reported_period)
    );
  }, [reportedPeriods, preAdvicePeriods, loadPreAdvice]);

  useEffect(() => {
    if (combinedPeriods.length > 0) {
      // ✅ ב-batch mode, התקופות כבר מכילות contact_id, אחרת נוסיף את userId
      const periodsForParent: ReportedPeriod[] = useBatchMode
        ? combinedPeriods.map((p) => ({
            reported_period: p.reported_period,
            policy_count: p.policy_count,
            user_id: (p as any).contact_id || userId!, // ✅ ב-batch יש contact_id
            type: p.type,
          }))
        : combinedPeriods.map((p) => ({
            reported_period: p.reported_period,
            policy_count: p.policy_count,
            user_id: userId!,
            type: p.type,
          }));
      onPeriodsLoaded?.(periodsForParent);
    }
  }, [combinedPeriods, userId, contactIds, useBatchMode]);

  const getOptionKey = (p: PeriodOption) => `${p.reported_period}|${p.type}`;

  const handleChange = (e: SelectChangeEvent) => {
    const val = e.target.value;
    const [reported_period, type] = val.split("|");
    if (reported_period && type) {
      onValueChange?.(reported_period, type as "REGULAR" | "PRE_ADVICE");
    }
  };

  if (loadingRegular || (loadPreAdvice && loadingPreAdvice)) {
    return <CircularProgress size={24} />;
  }

  if (combinedPeriods.length === 0) {
    return <Typography variant="body2">לא נמצאו תקופות</Typography>;
  }

  return (
    <Box>
      <Select
        value={value && type ? `${value}|${type}` : ""}
        onChange={handleChange}
        displayEmpty
        sx={{
          minWidth: 180,
          borderRadius: 4,
          "& .MuiSelect-select": {
            fontSize: "18px",
            paddingTop: { xs: "6px", sm: "10px" },
            paddingBottom: { xs: "6px", sm: "10px" },
          },
        }}
      >
        {combinedPeriods.map((period) => {
          const countText = showPolicyCount ? ` (${period.policy_count})` : "";
          const label =
            period.type === "PRE_ADVICE"
              ? `🅿️ טרום ייעוץ ${formatIsoDate(period.reported_period)}${countText}`
              : `${period.reported_period}${countText}`;
          const key = getOptionKey(period);

          return (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          );
        })}
      </Select>
    </Box>
  );
};
